import { useAppStore } from '@/stores/app';
import { Themes } from '@/themes';
import { ThemeConfigType, ThemeTypes } from '@/themes/typing';
import { isNullOrUnDef } from '@/utils/is';
import { useUpdatedColorPrimary } from '@/utils/hooks/themeHooks';
import { ProAliasToken, ProProvider } from '@ant-design/pro-components';
import { useSafeState } from 'ahooks';
import { theme as antdTheme, MappingAlgorithm } from 'antd';
import { ThemeProvider } from 'antd-style';
import { MessageInstance } from 'antd/es/message/interface';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { NotificationInstance } from 'antd/es/notification/interface';
import { cloneDeep, concat } from 'lodash';

import { useLayoutEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let message: MessageInstance,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  modal: Omit<ModalStaticFunctions, 'warn'>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  notification: NotificationInstance;

export default (props: React.PropsWithChildren) => {
  const { children } = props;
  //const [currentTheme, setCurrentTheme] = useSafeState<ThemeTypes>('light-default');
  const [currentTheme, setCurrentTheme] = useSafeState<ThemeConfigType | undefined>(undefined);

  const { isCompactMode, fontSize } = useAppStore(
    useShallow((state) => ({
      isCompactMode: state.layoutSettings.isCompactMode,
      fontSize: state.layoutSettings.fontSize,
    })),
  );
  const { darkTheme, lightTheme, themeMode, colorWeak, updatedAt, colorPrimary } = useAppStore(
    useShallow((state) => ({
      darkTheme: state.themeSettings.darkTheme,
      lightTheme: state.themeSettings.lightTheme,
      themeMode: state.themeSettings.themeMode,
      colorWeak: state.themeSettings.colorWeak,
      updatedAt: state.themeSettings.updatedAt,
      colorPrimary: state.themeSettings.colorPrimary,
    })),
  );

  const setColorPrimary = useAppStore((state) => state.setColorPrimary);

  // Inside the component
  useLayoutEffect(() => {
    if (colorWeak) {
      document.documentElement.className = 'colorWeak';
    } else {
      document.documentElement.className = '';
    }

    // Cleanup function to remove the filter when the component unmounts or colorWeak changes
    return () => {
      document.documentElement.className = '';
    };
  }, [colorWeak]); // Dependency array to re-run the effect when colorWeak changes

  useLayoutEffect(() => {
    let currentThemeName: ThemeTypes | undefined = undefined;
    if (themeMode === 'auto') {
      const hour = new Date().getHours();
      const isDayTime = hour >= 6 && hour < 18; // Day time from 6 AM to 6 PM
      currentThemeName = isDayTime ? lightTheme : darkTheme;
    } else if (themeMode === 'light') {
      currentThemeName = lightTheme;
    } else if (themeMode === 'dark') {
      currentThemeName = darkTheme;
    }
    if (!isNullOrUnDef(currentThemeName)) {
      const currentTheme = cloneDeep(Themes.getTheme(currentThemeName));

      if (currentTheme) {
        setCurrentTheme(currentTheme);
      }
    }
  }, [themeMode, darkTheme, lightTheme, setCurrentTheme]);

  const algorithm = useMemo(() => {
    let result: MappingAlgorithm[] =
      cloneDeep(currentTheme?.theme.algorithm as MappingAlgorithm[]) || [];
    if (isCompactMode && result) {
      result = concat(result, antdTheme.compactAlgorithm);
    }
    return result;
  }, [currentTheme?.theme.algorithm, isCompactMode]);

  const updatedColorPrimary = useUpdatedColorPrimary(colorPrimary, currentTheme, updatedAt);

  useLayoutEffect(() => {
    // Only update if the colors actually differ to prevent an infinite loop
    if (updatedColorPrimary && updatedColorPrimary.color !== colorPrimary.color) {
      setColorPrimary(updatedColorPrimary);
    }
  }, [updatedColorPrimary, setColorPrimary, colorPrimary.color]);

  return (
    <ThemeProvider
      themeMode={themeMode}
      defaultAppearance={currentTheme?.id}
      appearance={currentTheme?.id}
      theme={{
        token: {
          ...currentTheme?.theme.token,
          fontSize,
          ...(updatedColorPrimary ? { colorPrimary: updatedColorPrimary.color } : {}),
        },
        algorithm: algorithm,
        hashed: true,
        inherit: false,
      }}
      getStaticInstance={(instances) => {
        message = instances.message;
        modal = instances.modal;
        notification = instances.notification;
      }}
    >
      <ProProvider.Provider
        value={{
          token: {
            ...currentTheme?.theme.token,
            fontSize,
            ...(updatedColorPrimary ? { colorPrimary: updatedColorPrimary.color } : {}),
          } as ProAliasToken,
          dark: themeMode === 'dark',
          hashId: currentTheme?.id,
          hashed: true,
        }}
      >
        {children}
      </ProProvider.Provider>
    </ThemeProvider>
  );
};
